import { useState, useRef } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "@/firebase";
import { useAuth } from "@/contexts/AuthContext";
import { useUserProfile } from "@/contexts/UserProfileContext";
import {
  AlertTriangle, Upload, Calendar, ArrowRight,
  CheckSquare, Square, Loader2, CheckCircle2, X,
} from "lucide-react";

const MARKETPLACE_URL = "https://conecta.saluddigital360.com";

/**
 * PARQConsentGate
 * Se muestra cuando profile.f2Results.parqAlerta === true
 * y profile.parqConsentimiento es null/undefined.
 *
 * onResolved() — callback cuando el usuario completa una de las 3 vías.
 */
export function PARQConsentGate({ riskCount, onResolved }) {
  const { user } = useAuth();
  const { updateProfileFields } = useUserProfile();
  const [step, setStep] = useState("gate"); // "gate" | "disclaimer" | "uploading" | "done"
  const [checked, setChecked] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileRef = useRef();

  const saveConsent = async (type, url = null) => {
    const payload = {
      parqConsentimiento: type,
      parqConsentimientoAt: new Date().toISOString(),
    };
    if (url) payload.parqConsentimientoUrl = url;
    await updateDoc(doc(db, "users", user.uid), payload);
    if (typeof updateProfileFields === "function") {
      await updateProfileFields(payload);
    }
  };

  // ── Subir consentimiento médico ──────────────────────────────
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      const storageRef = ref(
        storage,
        `consents/${user.uid}/parq_consent_${Date.now()}_${file.name}`
      );
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      await saveConsent("signed", url);
      setStep("done");
      setTimeout(() => onResolved("signed"), 1500);
    } catch (err) {
      setUploadError("Error al subir el archivo. Intenta de nuevo.");
    } finally {
      setUploading(false);
    }
  };

  // ── Confirmar continuar bajo riesgo ─────────────────────────
  const handleOwnRisk = async () => {
    if (!checked) return;
    setUploading(true);
    try {
      await saveConsent("own_risk");
      setStep("done");
      setTimeout(() => onResolved("own_risk"), 1200);
    } catch { /* silent */ }
    setUploading(false);
  };

  // ── Done ────────────────────────────────────────────────────
  if (step === "done") {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-3 px-6">
        <div className="w-14 h-14 rounded-full flex items-center justify-center"
          style={{ background: "#CCFBF1" }}>
          <CheckCircle2 size={30} style={{ color: "#0F766E" }} />
        </div>
        <p className="text-sm font-semibold text-center" style={{ color: "#1E293B" }}>
          Información registrada correctamente
        </p>
      </div>
    );
  }

  // ── Disclaimer popup ────────────────────────────────────────
  if (step === "disclaimer") {
    return (
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm px-4 pb-6 sm:pb-0">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-bold" style={{ color: "#1E293B" }}>
              Confirmar decisión
            </h3>
            <button onClick={() => setStep("gate")}
              style={{ background: "none", border: "none", cursor: "pointer" }}>
              <X size={18} style={{ color: "#94A3B8" }} />
            </button>
          </div>

          <div className="rounded-xl p-4 mb-5"
            style={{ background: "#FFF7ED", border: "1.5px solid #FED7AA" }}>
            <p className="text-sm font-semibold mb-1" style={{ color: "#92400E" }}>
              Aviso legal
            </p>
            <p className="text-xs leading-relaxed" style={{ color: "#78350F" }}>
              Al continuar sin autorización médica, reconoces que WE360 y sus operadores
              no se hacen responsables de consecuencias sobre tu estado de salud derivadas
              del uso de la plataforma sin supervisión clínica.
            </p>
          </div>

          <button
            onClick={() => setChecked((c) => !c)}
            className="flex items-start gap-3 w-full mb-5 text-left"
            style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
            data-testid="parq-disclaimer-checkbox"
          >
            {checked
              ? <CheckSquare size={20} style={{ color: "#00628F", flexShrink: 0, marginTop: 1 }} />
              : <Square size={20} style={{ color: "#94A3B8", flexShrink: 0, marginTop: 1 }} />}
            <span className="text-xs font-semibold leading-relaxed" style={{ color: "#1E293B" }}>
              Entiendo y acepto que la plataforma no se hace responsable de mi estado
              de salud al avanzar sin autorización médica.
            </span>
          </button>

          <button
            onClick={handleOwnRisk}
            disabled={!checked || uploading}
            className="w-full py-3 rounded-xl font-bold text-sm transition-opacity"
            style={{
              background: checked ? "#1E293B" : "#E2E8F0",
              color: checked ? "white" : "#94A3B8",
              border: "none", cursor: checked ? "pointer" : "not-allowed",
            }}
            data-testid="parq-confirm-own-risk"
          >
            {uploading
              ? <span className="flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" /> Registrando...</span>
              : "Confirmar y continuar bajo mi riesgo"}
          </button>
        </div>
      </div>
    );
  }

  // ── Gate principal ──────────────────────────────────────────
  return (
    <div className="we-card p-5 mb-4" data-testid="parq-consent-gate">
      {/* Alerta */}
      <div className="flex items-start gap-3 rounded-xl p-4 mb-5"
        style={{ background: "#FEF3C7", border: "1.5px solid #FCD34D" }}>
        <AlertTriangle size={20} style={{ color: "#D97706", flexShrink: 0, marginTop: 1 }} />
        <div>
          <p className="text-sm font-bold mb-1" style={{ color: "#92400E" }}>
            ¡Acción requerida!
          </p>
          <p className="text-xs leading-relaxed" style={{ color: "#78350F" }}>
            Detectamos <strong>{riskCount} {riskCount === 1 ? "factor de riesgo" : "factores de riesgo"}</strong> en
            tu evaluación PAR-Q. Para garantizar tu seguridad, selecciona una de las
            siguientes opciones antes de continuar.
          </p>
        </div>
      </div>

      {/* Opción 1 — Subir consentimiento */}
      <input
        ref={fileRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        className="hidden"
        onChange={handleFileChange}
        data-testid="parq-consent-file-input"
      />
      <button
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        className="w-full flex items-center gap-3 p-4 rounded-xl mb-3 text-left transition-transform active:scale-[0.98]"
        style={{
          background: "linear-gradient(135deg,#00628F 0%,#0080A0 50%,#2A9D8F 100%)",
          border: "none", cursor: "pointer",
        }}
        data-testid="parq-upload-consent-btn"
      >
        <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
          style={{ background: "rgba(255,255,255,0.2)" }}>
          {uploading ? <Loader2 size={18} className="animate-spin text-white" />
            : <Upload size={18} style={{ color: "white" }} />}
        </div>
        <div>
          <p className="text-sm font-bold text-white">Subir consentimiento médico</p>
          <p className="text-xs text-white/70">PDF, JPG o PNG firmado por tu médico</p>
        </div>
        {uploadError && (
          <span className="text-xs text-red-200 ml-auto">{uploadError}</span>
        )}
      </button>

      {/* Opción 2 — Marketplace (próximamente) */}
      <div
        className="w-full flex items-center gap-3 p-4 rounded-xl mb-4"
        style={{
          background: "#F8FAFC",
          border: "1.5px solid #E2E8F0",
          opacity: 0.6,
        }}
        data-testid="parq-marketplace-btn"
      >
        <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
          style={{ background: "#E0F2F1" }}>
          <Calendar size={18} style={{ color: "#94A3B8" }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <p className="text-sm font-bold" style={{ color: "#94A3B8" }}>
              Agendar cita en el Marketplace
            </p>
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full"
              style={{ background: "#E0F2F1", color: "#0F766E" }}>
              Próximamente
            </span>
          </div>
          <p className="text-xs" style={{ color: "#CBD5E1" }}>
            Red de médicos certificados — disponible pronto
          </p>
        </div>
      </div>

      {/* Opción 3 — Bajo mi riesgo */}
      <div className="text-center">
        <button
          onClick={() => setStep("disclaimer")}
          className="text-xs underline"
          style={{ background: "none", border: "none", cursor: "pointer", color: "#94A3B8" }}
          data-testid="parq-own-risk-btn"
        >
          Continuar bajo mi propio riesgo
        </button>
      </div>
    </div>
  );
}
